import { getDomainCode, getErrorCode } from "@onadssuper/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { ShieldAlert, SearchX } from "lucide-react";
import { Button, EmptyState, ErrorState, PageSpinner } from "../../components/ui";
import { AuthLayout } from "../../features/auth/auth-layout";
import { requireUser } from "../../features/auth/guards";
import { AppShell } from "../../features/shell/app-shell";
import { CurrentWorkspaceContext } from "../../features/workspace/workspace-context";
import { isGlobalQueryKey, useTRPC } from "../../lib/trpc";
import { setWorkspaceId } from "../../lib/workspace-context";

export const Route = createFileRoute("/w/$slug")({
  beforeLoad: async ({ context, params, location }) => {
    const me = await requireUser(context, location.href);
    const membership = me.workspaces.find((w) => w.slug === params.slug) ?? null;
    // Switching workspace: drop every cached query that belongs to the old one.
    if (setWorkspaceId(membership?.id)) {
      context.queryClient.removeQueries({ predicate: (q) => !isGlobalQueryKey(q.queryKey) });
    }
    return { membership };
  },
  component: WorkspaceLayout,
});

function WorkspaceLayout() {
  const { membership } = Route.useRouteContext();
  if (!membership) return <NotAMember />;
  return <WorkspaceGate />;
}

function WorkspaceGate() {
  const trpc = useTRPC();
  const me = useQuery(trpc.me.get.queryOptions());
  const current = useQuery(trpc.workspaces.current.queryOptions());

  if (current.isPending || me.isPending) return <PageSpinner />;
  if (current.isError) {
    if (getDomainCode(current.error) === "MFA_REQUIRED") return <MfaRequired />;
    if (getErrorCode(current.error) === "FORBIDDEN") return <NotAMember />;
    return <ErrorState error={current.error} onRetry={() => void current.refetch()} />;
  }
  if (me.isError) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;

  return (
    <CurrentWorkspaceContext.Provider value={current.data}>
      <AppShell me={me.data} workspace={current.data}>
        <Outlet />
      </AppShell>
    </CurrentWorkspaceContext.Provider>
  );
}

/** Shown for unknown slugs and workspaces the user isn't a member of (indistinguishable on purpose). */
function NotAMember() {
  return (
    <AuthLayout title="Workspace not available">
      <EmptyState
        icon={<SearchX className="size-8" />}
        title="You don't have access to this workspace"
        description="It may not exist, or you may not be a member. Ask the workspace owner for an invitation."
        action={
          <Button asChild variant="secondary">
            <Link to="/">Go to my workspaces</Link>
          </Button>
        }
      />
    </AuthLayout>
  );
}

function MfaRequired() {
  return (
    <AuthLayout title="Two-factor authentication required">
      <EmptyState
        icon={<ShieldAlert className="size-8" />}
        title="This workspace requires two-factor authentication"
        description="Set up an authenticator app on your account to continue. It takes about a minute."
        action={
          <Button asChild>
            <Link to="/account/security">Set up two-factor authentication</Link>
          </Button>
        }
      />
    </AuthLayout>
  );
}
